/**
 * Pure builders for the Request Detail screen's activity and attempt-history
 * timelines, extracted so they can be tested directly and so the component
 * stays focused on state and interaction.
 */

import { IconName } from '../../shared/components/icon/icon-data.generated';
import {
  formatLocalDate,
  formatLocalTime,
  parseUtc,
} from '../../shared/utils/date-time/date-time.utils';
import { RequestDetail, RequestDetailAttempt } from '../models/request-detail';
import { maskSsnLastFive } from './identifier.utils';

export interface TimelineEvent {
  /**
   * Unique, namespaced track key (`note-3`, `adjusted-0`, `attempt-2`).
   * Events merge from several sources whose numeric ids can collide, so the
   * key carries its source.
   */
  id: string;
  variant: 'standard' | 'note';
  kind: 'system' | 'staff' | 'alert';
  icon: IconName;
  description: string;
  sortTime: number;
  timestamp: string;
  // Populated only when variant === 'note'
  noteBody?: string;
  authorName?: string;
  date?: string;
  time?: string;
}

export function formatTimelineTimestamp(value: string | Date): string {
  return `${formatLocalDate(value)} · ${formatLocalTime(value)}`;
}

/**
 * The submission + auto-search system events and one note event per staff
 * note. Unsorted — callers merge in other event sources and sort once.
 *
 * `autoSearchCount` is the match count captured when the request first
 * loaded; `request.patients` can't be used because adjusted searches and the
 * Search Patient Records handoff overwrite it, which would retroactively
 * rewrite this historical entry.
 */
export function buildRequestTimeline(
  request: RequestDetail,
  autoSearchCount: number,
): TimelineEvent[] {
  const requestTime = parseUtc(request.createdOnUtc);
  return [
    {
      id: 'submitted',
      variant: 'standard',
      kind: 'system',
      icon: 'upload',
      description:
        'Request submitted by patient via portal. Could not be automatically matched and was sent to the manual review queue.',
      sortTime: requestTime.getTime(),
      timestamp: formatTimelineTimestamp(request.createdOnUtc),
    },
    {
      id: 'auto-search',
      variant: 'standard',
      kind: 'system',
      icon: 'search',
      description: `Auto-search completed. ${autoSearchCount} potential match${autoSearchCount === 1 ? '' : 'es'} found.`,
      sortTime: requestTime.getTime(),
      timestamp: formatTimelineTimestamp(request.createdOnUtc),
    },
    ...request.notes.map((note): TimelineEvent => {
      const noteTime = parseUtc(note.createdOnUtc);
      return {
        id: `note-${note.patientPortalUserRequestNoteId}`,
        variant: 'note',
        kind: 'staff',
        icon: 'sticky_note_2',
        description: 'Note added by staff.',
        sortTime: noteTime.getTime(),
        timestamp: formatTimelineTimestamp(note.createdOnUtc),
        noteBody: note.note,
        authorName: note.createdByDisplayName?.trim() || 'Staff',
        date: formatLocalDate(note.createdOnUtc),
        time: formatLocalTime(note.createdOnUtc),
      };
    }),
  ];
}

/**
 * The Attempt History card's events: one per verification attempt plus the
 * admin-notification entry, in attempt order.
 *
 * NOTE: "final attempt" (the one that suspended the account) is inferred
 * purely from position in the list — this assumes the API returns the
 * complete, untruncated attempt history. If attempts are ever paginated or
 * capped server-side, the suspension flag must come from the API instead.
 */
export function buildAttemptTimeline(
  attempts: RequestDetailAttempt[] | null | undefined,
): TimelineEvent[] {
  const ordered = [...(attempts ?? [])].sort((a, b) => a.attemptNumber - b.attemptNumber);
  if (ordered.length === 0) {
    return [];
  }

  const events: TimelineEvent[] = ordered.map((attempt, index) => {
    const isFinalAttempt = index === ordered.length - 1;
    const suffix = isFinalAttempt ? ' Account suspended and flagged.' : '';
    return {
      id: `attempt-${attempt.attemptNumber}`,
      variant: 'standard',
      kind: isFinalAttempt ? 'alert' : 'system',
      icon: isFinalAttempt ? 'warning' : 'upload',
      description: `Attempt ${attempt.attemptNumber}: ${formatAttemptIdentifier(attempt)} — No match found.${suffix}`,
      sortTime: parseUtc(attempt.createdOnUtc).getTime(),
      timestamp: formatTimelineTimestamp(attempt.createdOnUtc),
    };
  });

  const finalAttempt = ordered[ordered.length - 1];
  events.push({
    id: 'attempt-notified',
    variant: 'standard',
    kind: 'system',
    icon: 'notifications',
    description: 'Admin team notified. Linking suspended for this portal account.',
    sortTime: parseUtc(finalAttempt.createdOnUtc).getTime(),
    timestamp: formatTimelineTimestamp(finalAttempt.createdOnUtc),
  });

  return events;
}

function formatAttemptIdentifier(attempt: RequestDetailAttempt): string {
  if (attempt.medicalRecordNumber) {
    return `MRN ${attempt.medicalRecordNumber}`;
  }

  if (attempt.lastFiveSocialSecurityNumber) {
    return `SSN ${maskSsnLastFive(attempt.lastFiveSocialSecurityNumber)}`;
  }

  return 'No identifier provided';
}
